import {
  normalizePastedMarkdown,
  parseDocumentIdentifier,
  parseMarkdown,
  serializeMarkdown,
} from "@wiki/core";
import { DOMParser as PMDOMParser, Fragment, Slice } from "prosemirror-model";
import { Plugin, PluginKey, type EditorState, type Transaction } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import type { EditorContext } from "./context";
import { insertEmbed, insertMention, isEmbeddableUrl } from "./commands/media";
import { setLink } from "./commands/marks";
import { isKnownLanguage } from "./commands/code";
import { isSingleUrl, looksLikeMarkdown, urlList } from "./paste-heuristic";
import { marks, nodes, schema, isInCode } from "./schema";
import { uploadFiles } from "./plugins/upload";
import type { MentionSearchResult } from "./types";

/** What the paste path decided, exposed so the behaviour is testable. */
export type PasteDecision =
  | "plain-into-code"
  | "structured-html"
  | "link-over-selection"
  | "document-mention"
  | "collection-mention"
  | "url-link"
  | "code-block-from-hint"
  | "inline-code-from-hint"
  | "markdown"
  | "html";

export interface ClipboardFlavours {
  text: string;
  html: string;
  /** Some code editors publish the language of the copied text. */
  languageHint: string | null;
  shift: boolean;
}

const STRUCTURE_MARKERS = ["data-pm-slice", "data-ai-response", "data-assistant-response"];

export function hasStructureMarker(html: string): boolean {
  return STRUCTURE_MARKERS.some((marker) => html.includes(marker));
}

/**
 * §8 — HTML whose structure beats its plain-text rendering: an image or a table
 * in the markup means the DOM parser keeps more than the markdown path would.
 */
export function htmlBeatsMarkdown(html: string): boolean {
  return /<img[\s>]/i.test(html) || /<table[\s>]/i.test(html);
}

export function readClipboard(event: ClipboardEvent, shift: boolean): ClipboardFlavours {
  const data = event.clipboardData;
  const vscode = data?.getData("vscode-editor-data") ?? "";
  let languageHint: string | null = null;
  if (vscode !== "") {
    try {
      const parsed = JSON.parse(vscode) as { mode?: unknown };
      if (typeof parsed.mode === "string") languageHint = parsed.mode;
    } catch {
      languageHint = null;
    }
  }
  return {
    text: data?.getData("text/plain") ?? "",
    html: data?.getData("text/html") ?? "",
    languageHint,
    shift,
  };
}

export interface InternalTarget {
  kind: "document" | "collection";
  /** The id or urlId in the path. */
  id: string;
  fragment: string | null;
}

/** Recognise our own document and collection URLs (§8.3). */
export function internalTarget(url: string, origin: string): InternalTarget | null {
  let parsed: URL;
  try {
    parsed = new URL(url, origin);
  } catch {
    return null;
  }
  if (parsed.origin !== origin) return null;
  const fragment = parsed.hash === "" ? null : parsed.hash.slice(1);
  const docMatch = /^\/(?:doc|d)\/([^/]+)$/.exec(parsed.pathname);
  if (docMatch?.[1] !== undefined) {
    const identifier = parseDocumentIdentifier(decodeURIComponent(docMatch[1]));
    if (identifier) return { kind: "document", id: identifier.value, fragment };
  }
  const collectionMatch = /^\/collection\/([^/]+)$/.exec(parsed.pathname);
  if (collectionMatch?.[1] !== undefined) {
    return { kind: "collection", id: decodeURIComponent(collectionMatch[1]), fragment };
  }
  return null;
}

/** The paste menu shown after a URL becomes a link (§8.3). */
export interface PasteMenuState {
  href: string;
  from: number;
  to: number;
  /** Several URLs pasted at once may become a run of embeds. */
  urls: string[] | null;
}

export const pasteMenuKey = new PluginKey<PasteMenuState | null>("wikiPasteMenu");

export function pasteMenuState(state: EditorState): PasteMenuState | null {
  return pasteMenuKey.getState(state) ?? null;
}

export function closePasteMenu(view: EditorView): void {
  view.dispatch(view.state.tr.setMeta(pasteMenuKey, null));
}

function markdownToSlice(text: string, state: EditorState): Slice {
  const doc = parseMarkdown(normalizePastedMarkdown(text));
  const content = doc.content;
  // A single paragraph pastes as inline content into the current block; a
  // single list merges with an adjacent list of the same type (§8.5).
  if (content.childCount === 1) {
    const only = content.child(0);
    if (only.type === nodes.paragraph) return new Slice(only.content, 0, 0);
    if (
      (only.type === nodes.bulletList ||
        only.type === nodes.orderedList ||
        only.type === nodes.checkboxList) &&
      state.selection.$from.node(-1)?.type === only.type
    ) {
      return new Slice(only.content, 0, 0);
    }
  }
  return new Slice(content, 0, 0);
}

function htmlToSlice(html: string, state: EditorState): Slice {
  const parsed = new window.DOMParser().parseFromString(html, "text/html");
  return PMDOMParser.fromSchema(schema).parseSlice(parsed.body, {
    context: state.selection.$from,
  });
}

interface PasteOutcome {
  decision: PasteDecision;
  apply?: (view: EditorView) => void;
}

/**
 * `handlePaste` in the order of `editor.md` §8: the first branch that applies
 * wins. Pure enough to test without a view — the decision and the mutation are
 * separate.
 */
export function decidePaste(
  state: EditorState,
  clipboard: ClipboardFlavours,
  context: EditorContext,
  origin: string,
): PasteOutcome {
  const { text, html, languageHint, shift } = clipboard;

  // 1. Inside code, the plain flavour goes in verbatim.
  if (isInCode(state)) {
    return {
      decision: "plain-into-code",
      apply: (view) => {
        view.dispatch(view.state.tr.insertText(text).scrollIntoView());
      },
    };
  }

  // 2. A slice we (or an assistant) produced already has the right structure.
  if (!shift && html !== "" && hasStructureMarker(html)) {
    return { decision: "structured-html" };
  }

  if (!shift && isSingleUrl(text)) {
    const href = text.trim();
    // 3a. A selection becomes the link text.
    if (!state.selection.empty) {
      return {
        decision: "link-over-selection",
        apply: (view) => {
          setLink(href)(view.state, view.dispatch, view);
        },
      };
    }
    const target = internalTarget(href, origin);
    // 3b/3c. Internal document and collection URLs become mentions.
    if (target?.kind === "document") {
      return {
        decision: "document-mention",
        apply: (view) => {
          void insertDocumentMention(view, href, target, context);
        },
      };
    }
    if (target?.kind === "collection" && target.fragment === null) {
      return {
        decision: "collection-mention",
        apply: (view) => {
          insertMention({
            modelId: target.id,
            type: "collection",
            label: target.id,
            href,
          })(view.state, view.dispatch, view);
        },
      };
    }
    // 3d. Anything else: a link, plus the paste menu.
    return {
      decision: "url-link",
      apply: (view) => {
        insertLinkWithMenu(view, href);
      },
    };
  }

  // 4. A language hint that is not markdown.
  if (!shift && languageHint !== null && languageHint !== "markdown" && text !== "") {
    if (text.includes("\n")) {
      const language = isKnownLanguage(languageHint) ? languageHint : "";
      return {
        decision: "code-block-from-hint",
        apply: (view) => {
          const block = nodes.codeBlock.create({ language }, schema.text(text));
          view.dispatch(view.state.tr.replaceSelectionWith(block).scrollIntoView());
        },
      };
    }
    return {
      decision: "inline-code-from-hint",
      apply: (view) => {
        const node = schema.text(text, [marks.code.create()]);
        view.dispatch(view.state.tr.replaceSelectionWith(node, false).scrollIntoView());
      },
    };
  }

  // 5. The markdown path — unless the HTML flavour is structurally better.
  const markdownWanted =
    shift || languageHint === "markdown" || html === "" || looksLikeMarkdown(text);
  if (text !== "" && markdownWanted && !(html !== "" && htmlBeatsMarkdown(html))) {
    const urls = shift ? null : urlList(text);
    return {
      decision: "markdown",
      apply: (view) => {
        const slice = markdownToSlice(text, view.state);
        const tr: Transaction = view.state.tr.replaceSelection(slice);
        if (urls) {
          tr.setMeta(pasteMenuKey, {
            href: urls[0] ?? "",
            from: view.state.selection.from,
            to: tr.selection.to,
            urls,
          } satisfies PasteMenuState);
        }
        view.dispatch(tr.scrollIntoView());
      },
    };
  }

  // 6. The HTML flavour through the DOM parser.
  if (html !== "") {
    return {
      decision: "html",
      apply: (view) => {
        view.dispatch(
          view.state.tr.replaceSelection(htmlToSlice(html, view.state)).scrollIntoView(),
        );
      },
    };
  }
  return { decision: "structured-html" };
}

function insertLinkWithMenu(view: EditorView, href: string): void {
  const from = view.state.selection.from;
  const node = schema.text(href, [marks.link.create({ href })]);
  const tr = view.state.tr.replaceSelectionWith(node, false);
  tr.setMeta(pasteMenuKey, {
    href,
    from,
    to: from + node.nodeSize,
    urls: null,
  } satisfies PasteMenuState);
  view.dispatch(tr.scrollIntoView());
}

async function resolveDocument(
  href: string,
  context: EditorContext,
): Promise<MentionSearchResult | null> {
  try {
    return (await context.resolveDocumentUrl?.(href)) ?? null;
  } catch {
    return null;
  }
}

async function insertDocumentMention(
  view: EditorView,
  href: string,
  target: InternalTarget,
  context: EditorContext,
): Promise<void> {
  const resolved = await resolveDocument(href, context);
  if (!resolved) {
    // On failure, a plain link — the spec's fallback.
    insertLinkWithMenu(view, href);
    return;
  }
  const mention: MentionSearchResult = {
    ...resolved,
    type: "document",
    modelId: resolved.modelId === "" ? target.id : resolved.modelId,
    href,
  };
  insertMention(mention)(view.state, view.dispatch, view);
}

/** Copy and cut put markdown on the clipboard as `text/plain`. */
export function sliceToMarkdown(slice: Slice): string {
  const content = slice.content;
  const first = content.firstChild;
  const isInline = first?.isInline === true;
  const doc = nodes.doc.createChecked(
    null,
    isInline ? Fragment.from(nodes.paragraph.create(null, content)) : content,
  );
  return serializeMarkdown(doc).trimEnd();
}

export interface PastePluginOptions {
  context: EditorContext;
  /** Injectable for tests; the browser's own origin by default. */
  origin?: string;
}

export function pastePlugin(options: PastePluginOptions): Plugin<PasteMenuState | null> {
  const { context } = options;
  let shiftHeld = false;
  return new Plugin<PasteMenuState | null>({
    key: pasteMenuKey,
    state: {
      init: () => null,
      apply(tr, value) {
        if (tr.getMeta(pasteMenuKey) !== undefined) {
          return tr.getMeta(pasteMenuKey) as PasteMenuState | null;
        }
        // The menu is dismissed by the next edit or selection change (§8,
        // open question 2 — we choose "transient").
        if (tr.docChanged || tr.selectionSet) return null;
        return value;
      },
    },
    props: {
      handleDOMEvents: {
        keydown: (_view, event) => {
          shiftHeld = event.shiftKey;
          return false;
        },
        keyup: (_view, event) => {
          shiftHeld = event.shiftKey;
          return false;
        },
      },
      handlePaste(view, event) {
        if (context.readOnly) return false;
        const files = Array.from(event.clipboardData?.files ?? []);
        if (files.length > 0) {
          void uploadFiles(view, files, view.state.selection.from, context);
          return true;
        }
        const clipboard = readClipboard(event, shiftHeld);
        if (clipboard.text === "" && clipboard.html === "") return false;
        const outcome = decidePaste(
          view.state,
          clipboard,
          context,
          options.origin ?? window.location.origin,
        );
        if (!outcome.apply) return false;
        outcome.apply(view);
        return true;
      },
      handleDrop(view, event) {
        if (context.readOnly) return false;
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (files.length === 0) return false;
        const at = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        void uploadFiles(view, files, at ?? view.state.selection.from, context);
        event.preventDefault();
        return true;
      },
      clipboardTextSerializer: (slice) => sliceToMarkdown(slice),
    },
  });
}

/** Actions offered by the paste menu. */
export function applyPasteMenuChoice(
  view: EditorView,
  menu: PasteMenuState,
  choice: "link" | "embed" | "mention",
): void {
  if (choice === "link") {
    closePasteMenu(view);
    return;
  }
  if (choice === "embed") {
    const urls = menu.urls ?? [menu.href];
    const embeddable = urls.filter((url) => isEmbeddableUrl(url));
    if (embeddable.length === 0) {
      closePasteMenu(view);
      return;
    }
    const tr = view.state.tr.delete(menu.from, Math.min(menu.to, view.state.doc.content.size));
    view.dispatch(tr.setMeta(pasteMenuKey, null));
    for (const url of embeddable) insertEmbed(url)(view.state, view.dispatch, view);
    return;
  }
  const tr = view.state.tr.delete(menu.from, Math.min(menu.to, view.state.doc.content.size));
  view.dispatch(tr.setMeta(pasteMenuKey, null));
  insertMention({
    modelId: menu.href,
    type: "url",
    label: menu.href,
    href: menu.href,
  })(view.state, view.dispatch, view);
}
